<template>
  <div class="mx-auto mt-4 w-96">
    <form class="form-wrapper" @submit.prevent="onSubmit">
      <InputWrapped
        name="email"
        :label="t('input.label.email')"
        :placeholder="t('input.label.email')"
        v-model="email"
        v-bind="emailAttrs"
        :error="errors.email"
        autocomplete="username"
        :disabled="isSendForgotPasswordFetching"
      />
      <Button type="submit" :disabled="isSendForgotPasswordFetching">
        <Loader2
          v-if="isSendForgotPasswordFetching"
          class="mr-2 h-4 w-4 animate-spin stroke-white"
        />
        {{ t('forgotPassword.buttonLabel') }}
      </Button>
      <InfoBar
        v-if="sendForgotPasswordError"
        variant="destructive"
        :title="t('common.error')"
        :message="sendForgotPasswordError?.message"
        @close="sendForgotPasswordError = null"
      />
      <InfoBar
        v-if="sendForgotPasswordData?.message"
        variant="success"
        :title="t('common.success')"
        :message="sendForgotPasswordData?.message"
        @close="sendForgotPasswordData = null"
      />
    </form>
  </div>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { useAccountStore } from '@stores/account.store';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
// Components
import InfoBar from '@generics/InfoBar.vue';
import InputWrapped from '@modules/InputWrapped/InputWrapped.vue';
import Button from '@ui/button/Button.vue';

// Icons
import { Loader2 } from 'lucide-vue-next';
import { watch } from 'vue';

const { t } = useI18n();

interface ForgotPasswordForm {
  email: string;
}

const accountStore = useAccountStore();
const { sendForgotPasswordData, sendForgotPasswordError, isSendForgotPasswordFetching } =
  storeToRefs(accountStore);
const schema = toTypedSchema(
  yup.object({
    email: yup.string().required(t('errors.required')).email(t('errors.invalidEmail')),
  }),
);
const { errors, handleSubmit, defineField, values } = useForm<ForgotPasswordForm>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: {
    email: accountStore.guestEmail || '',
  },
});

const [email, emailAttrs] = defineField('email', {
  validateOnModelUpdate: false,
  validateOnBlur: true,
});

const onSubmit = handleSubmit((values) => {
  accountStore.sendForgotPassword(values.email);
});

watch(email, (newValue) => {
  if (newValue !== accountStore.guestEmail) {
    accountStore.guestEmail = newValue;
  }
});

watch(
  values,
  () => {
    sendForgotPasswordError.value = null;
    sendForgotPasswordData.value = null;
  },
  { deep: true },
);
</script>
